/**
 * Daily Giveaway store. Hydrated from the backend `giveaway` section of the
 * initial pass data (ENG-2205) and refreshed via /giveaway/{dealId}; the draw
 * happens server-side through the existing check-in endpoint (ENG-2206) via the
 * submitEntry action.
 *
 * All state transitions go through mutations (strict-mode safe); actions
 * orchestrate only.
 */

import moment from 'moment-timezone';
import $api from '../api';
import { DEFAULT_RECOVERY_COPY } from '@/components/shared/pass/validation/recoveryCopy';
import ticketHelpers from '@/components/modules/Tickets/helpers';
import { summarizeWeek } from '@/components/shared/pass/hours';

/* -------------------- Brand + constants -------------------- */

const AZ_ORANGE = '#FF9900';
const AZ_NAVY = '#232F3E';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/* -------------------- Cadence engine -------------------- */

/**
 * Derive the human period label for the pass title from the program's actual
 * date range — handles same-month ("September"), cross-month ("Sep–Oct") and
 * cross-year ("Sep 2026 – Feb 2027") ranges for any cadence.
 */
function deriveTitle(cadence, startDate, totalPeriods) {
  const noun = { hourly: 'Hourly', daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly' }[cadence];
  let end;
  if (cadence === 'hourly') {
    return `${noun} Giveaway · ${MONTH_ABBR[startDate.getMonth()]} ${startDate.getDate()}`;
  }
  if (cadence === 'weekly') {
    end = addDays(startDate, totalPeriods * 7 - 1);
  } else if (cadence === 'monthly') {
    end = new Date(startDate.getFullYear(), startDate.getMonth() + totalPeriods - 1, 1);
  } else {
    end = addDays(startDate, totalPeriods - 1);
  }
  const sM = startDate.getMonth(); const sY = startDate.getFullYear();
  const eM = end.getMonth(); const eY = end.getFullYear();
  let label;
  if (sY !== eY) {
    label = `${MONTH_ABBR[sM]} ${sY} – ${MONTH_ABBR[eM]} ${eY}`;
  } else if (sM !== eM) {
    label = `${MONTH_ABBR[sM]}–${MONTH_ABBR[eM]}`;
  } else {
    label = MONTH_NAMES[sM];
  }
  return `${noun} Giveaway · ${label}`;
}

const addDays = (start, offset) => {
  const d = new Date(start);
  d.setDate(d.getDate() + offset);
  return d;
};

function buildDailyCtx({ cadence, totalPeriods, currentPeriod, title, calendarTitle }) {
  return {
    cadence, totalPeriods, currentPeriod, title, calendarTitle,
    calendarMeta: `${totalPeriods} days · ${totalPeriods} businesses`,
    listTitle: `All ${totalPeriods} businesses`,
    listLockCopy: "Check-in is unlocked only for today's featured business. Tap any other stop for details — you'll be able to check in on that business's featured day.",
    meterWindow: 'this month',
    entryUnit: 'day',
    grainUnit: 'hour', // per-grain inventory: a daily giveaway is editable per hour
    todayChip: 'Today',
    ctaEnter: 'Check in to enter',
    tryAgainSoon: 'Try again tomorrow.'
  };
}

/**
 * Build the cadence context from the backend program block.
 * @param {string} cadence
 * @param {{startDate: Date, totalPeriods: number, currentPeriod: number, startTime?: string}} program
 */
function buildCadenceCtx(cadence, program) {
  if (cadence === 'hourly') {
    const currentPeriod = program.currentPeriod;
    const totalPeriods = program.totalPeriods;
    return {
      cadence, totalPeriods, currentPeriod,
      title: deriveTitle('hourly', program.startDate, totalPeriods),
      calendarTitle: "Today's schedule",
      calendarMeta: `${totalPeriods} hours · ${totalPeriods} businesses`,
      listTitle: `All ${totalPeriods} businesses`,
      listLockCopy: "Check-in is unlocked only for this hour's featured business. Tap any other stop for details — you'll be able to check in during that business's featured hour.",
      meterWindow: 'today',
      entryUnit: 'hour',
      grainUnit: 'hour',
      todayChip: 'This hour',
      ctaEnter: 'Check in to enter this hour',
      tryAgainSoon: 'Come back next hour.'
    };
  }
  if (cadence === 'weekly') {
    const currentPeriod = program.currentPeriod;
    const totalPeriods = program.totalPeriods;
    return {
      cadence, totalPeriods, currentPeriod,
      title: deriveTitle('weekly', program.startDate, totalPeriods),
      calendarTitle: 'Weekly schedule',
      calendarMeta: `${totalPeriods} weeks · ${totalPeriods} businesses`,
      listTitle: `All ${totalPeriods} businesses`,
      listLockCopy: "Check-in is unlocked only for this week's featured business. Tap any other stop for details — you'll be able to check in on that business's featured week.",
      meterWindow: 'this series',
      entryUnit: 'week',
      grainUnit: 'day', // weekly giveaway is editable per day
      todayChip: 'This week',
      ctaEnter: 'Check in to enter this week',
      tryAgainSoon: 'Come back next week.'
    };
  }
  if (cadence === 'monthly') {
    const currentPeriod = program.currentPeriod;
    const totalPeriods = program.totalPeriods;
    return {
      cadence, totalPeriods, currentPeriod,
      title: deriveTitle('monthly', program.startDate, totalPeriods),
      calendarTitle: 'Monthly schedule',
      calendarMeta: `${totalPeriods} months · ${totalPeriods} businesses`,
      listTitle: `All ${totalPeriods} businesses`,
      listLockCopy: "Check-in is unlocked only for this month's featured business. Tap any other stop for details — you'll be able to check in on that business's featured month.",
      meterWindow: 'this season',
      entryUnit: 'month',
      grainUnit: 'day', // monthly giveaway is editable per day
      todayChip: 'This month',
      ctaEnter: 'Check in to enter this month',
      tryAgainSoon: 'Come back next month.'
    };
  }
  const startDate = program.startDate;
  const totalPeriods = program.totalPeriods;
  const currentPeriod = program.currentPeriod;
  return buildDailyCtx({
    cadence: 'daily',
    totalPeriods,
    currentPeriod,
    title: deriveTitle('daily', startDate, totalPeriods),
    calendarTitle: `${MONTH_NAMES[startDate.getMonth()]} calendar`
  });
}

/** A period's calendar labels, formatted once from its own starts_at (program tz). */
function periodLabels(startsAt, featuredDay, cadence, tz) {
  if (cadence === 'weekly') {
    return { periodLabel: `Week ${featuredDay}`, periodShort: `W${featuredDay}`, periodPrefix: 'WEEK' };
  }
  const m = startsAt ? moment.utc(startsAt).tz(tz) : null;
  if (!m) return { periodLabel: '', periodShort: '', periodPrefix: '' };
  if (cadence === 'hourly') {
    const t = m.format('h A');
    return { periodLabel: t, periodShort: t, periodPrefix: '' };
  }
  if (cadence === 'monthly') {
    return { periodLabel: m.format('MMMM'), periodShort: m.format('MMM'), periodPrefix: '' };
  }
  return { periodLabel: m.format('MMM D'), periodShort: m.format('D'), periodPrefix: m.format('MMM').toUpperCase() };
}

/* -------------------- Live payload mapping -------------------- */

const GOOGLE_DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

/** Google Places hours ({periods: [{open:{day,time}, close:{day,time}}]}) → WeeklyHours. */
function googleToWeekly(hours) {
  const periods = hours?.periods || [];
  if (!periods.length) return null;
  const weekly = {};
  for (const p of periods) {
    const openDay = p.open?.day;
    const openTime = p.open?.time;
    if (openDay == null || !openTime) continue;
    const key = GOOGLE_DAY_KEYS[openDay];
    const open = `${openTime.slice(0, 2)}:${openTime.slice(2)}`;
    let close;
    if (!p.close?.time || (p.close.time === '0000' && openTime === '0000' && p.close.day === openDay)) {
      close = '24:00'; // open 24 hours
    } else {
      const closeH = parseInt(p.close.time.slice(0, 2), 10);
      // Overnight close belongs to the opening day, expressed past 24:00.
      const spillsOver = p.close.day !== openDay || p.close.time <= openTime;
      close = `${String(spillsOver ? closeH + 24 : closeH).padStart(2, '0')}:${p.close.time.slice(2)}`;
    }
    (weekly[key] = weekly[key] || []).push([open, close]);
  }
  return Object.keys(weekly).length ? weekly : null;
}

const firstMediaUrl = media =>
  (Array.isArray(media) ? media : []).map(m => m?.url || m?.originalURL).find(Boolean) || null;

// '' methods mean "no validation" (virtual gift-card prizes) → null spec.
// GPS-bearing live specs run the REAL geolocation flow (never the demo one).
const liveSpec = (spec, keepNoMethod = false) => {
  if (!spec) return null;
  if (!spec.method && !keepNoMethod) return null;
  return spec.method?.includes('gps') ? { ...spec, realGps: true } : spec;
};

/** One backend `periods[]` entry → the venue shape every getter/layout consumes. */
function mapVenue(entry) {
  const venue = entry.venue || {};
  const weekly = googleToWeekly(venue.hours);
  return {
    id: venue.id,
    name: venue.name,
    kinds: venue.kinds || [],
    address: venue.address,
    latitude: venue.latitude,
    longitude: venue.longitude,
    hoursWeekly: weekly,
    weekdayText: venue.hours?.weekday_text || [],
    hours: weekly ? summarizeWeek(weekly) : '',
    distanceMeters: undefined, // needs shared location; the layout hides distance without it
    img: firstMediaUrl(venue.media),
    featuredDay: entry.period,
    description: venue.description,
    phone: venue.phone,
    website: venue.website,
    coupons: venue.coupons || [],
    audioUrl: null,
    audioLabel: null,
    videoUrl: null,
    videoPoster: null,
    checkIn: liveSpec(entry.checkIn, true),
    checkInRepeat: !!entry.checkIn?.repeat,
    prize: entry.prize
      ? { ...entry.prize, checkIn: liveSpec(entry.prize.checkIn) }
      : null,
    startsAt: entry.startsAt,
    endsAt: entry.endsAt
  };
}

/** Backend giveaway payload → the SET_STATE patch for live mode. */
function payloadPatch(payload) {
  const program = payload.program || {};
  const me = payload.me || {};
  const minutes = program.redemption?.minutes || 60;
  const wonAtMs = me.wonAt ? new Date(me.wonAt).getTime() : null;
  const expiresAtMs = me.expiresAt ? new Date(me.expiresAt).getTime() : (wonAtMs ? wonAtMs + minutes * 60000 : null);
  const serverResult = me.todayResult || 'unentered';
  const winExpired = serverResult === 'won' && !me.redeemed && expiresAtMs !== null && expiresAtMs <= Date.now() && !!me.prizeTicket?.redeemOnDashboard;
  return {
    active: true,
    dealId: program.dealId,
    cadence: program.cadence || 'daily',
    program: {
      startDate: program.startDate || null,
      timezone: program.timezone || null,
      startTime: program.startTime || null,
      totalPeriods: program.totalPeriods || 0,
      currentPeriod: program.currentPeriod || 0,
      revealStyle: program.revealStyle || 'gift',
      howItWorks: program.howItWorks || null,
      description: program.description || null,
      recoveryCopy: program.recoveryCopy || null,
      heroMedia: program.heroMedia || null,
      redemptionMinutes: program.redemption?.minutes || 60
    },
    venues: (payload.periods || []).map(mapVenue),
    prizeProductIds: program.prizeProductIds || [],
    pastOutcomesData: payload.pastOutcomes || {},
    claimed: payload.claimed || 0,
    totalPrizes: payload.totalPrizes || 0,
    todayResult: winExpired ? 'expired' : serverResult,
    wonAt: payload.me?.wonAt ? new Date(payload.me.wonAt).getTime() : null,
    checkedInAt: payload.me?.checkedInAt || null,
    redeemed: !!payload.me?.redeemed,
    redeemedAt: payload.me?.prizeTicket?.redeemed ? moment.utc(payload.me.prizeTicket.redeemed).valueOf() : null,
    expiresAtData: payload.me?.expiresAt ? new Date(payload.me.expiresAt).getTime() : null,
    prizeTicket: payload.me?.prizeTicket || null,
    colors: {
      primary: program.colors?.primary || AZ_ORANGE,
      accent: program.colors?.accent || AZ_NAVY
    }
  };
}

// Same conversion table as store/orderProducts.js getTicketTimeout — keep in sync.
const ticketHideTimeoutMs = settings => {
  const { type, amount } = settings || {};
  switch (type) {
    case null:
    case undefined:
      return undefined; // never hide
    case 'immediate':
      return 10 * 60 * 1000; // platform-standard 10-minute grace
    case 'days':
      return Number(amount) * 24 * 60 * 60 * 1000;
    case 'hours':
      return Number(amount) * 60 * 60 * 1000;
    case 'minutes':
      return Number(amount) * 60 * 1000;
    default:
      return 10 * 60 * 1000;
  }
};

function couponState(p, deal) {
  const ops = p.order_products || [];
  const hideMs = ticketHideTimeoutMs(p.ticket_settings?.hide_ticket?.after_redemption);
  const liveRedeemed = ops.some(op => op.redeemed &&
    (hideMs === undefined || moment.utc(op.redeemed).valueOf() + hideMs > Date.now()));
  if (liveRedeemed) return 'view';
  if (ops.some(op => ticketHelpers.isAvailable(op, p, deal))) return 'redeem';
  if (ops.some(op => !op.redeemed)) return 'unavailable';
  if (ops.some(op => op.redeemed)) return 'history';
  return 'redeem';
}

/* -------------------- Vuex module -------------------- */

const getDefaultState = () => ({
  active: false,
  dealId: null,
  cadence: 'daily',
  stage: 'idle', // idle | checkin | reveal | product | product-validate | product-confirm | ticket | lose
  todayResult: 'unentered', // unentered | won | lost | expired
  pendingOutcome: null, // 'won' | 'lost' | null
  wonAt: null,
  checkedInAt: null,
  redeemed: false,
  redeemedAt: null, // ms epoch of the FIRST redemption — the hide_ticket clock
  colors: { primary: AZ_ORANGE, accent: AZ_NAVY },
  payload: null, // raw backend section, held until the matching deal page activates it
  program: null,
  venues: [],
  prizeProductIds: [],
  pastOutcomesData: {},
  claimed: 0,
  totalPrizes: 0,
  expiresAtData: null, // server redemption deadline for MY current win (ms epoch)
  prizeTicket: null, // real order product/ticket for TicketsModal (me.prizeTicket)
  submitting: false
});

const state = getDefaultState();

const getters = {
  hasGiveaway: s => s.active,
  colors: (s, g, rootState) => ({
    primary: rootState.offer?.styles?.colors?.primary?.main
      || rootState.partner?.styles?.colors?.primary?.main
      || AZ_ORANGE,
    accent: s.colors?.accent || AZ_NAVY
  }),
  giveawayDealId: s => s.payload?.program?.dealId ?? null,
  redemptionMinutes: s => s.program?.redemptionMinutes || 60,
  // Server redemption deadline — the layout's countdown prefers it over
  // wonAt + redemptionMinutes when present.
  expiresAt: s => s.expiresAtData,
  pastOutcomes: s => s.pastOutcomesData,
  revealStyle: s => s.program?.revealStyle || 'gift',
  howItWorks: s => s.program?.howItWorks,
  passDescription: (s, g, rootState, rootGetters) =>
    rootGetters['deals/find'](g.giveawayDealId)?.mobile_pass_description ?? s.program?.description,
  // Pass header image: program hero, else offer Header/Hero media.
  headerImage: (s, g, rootState) => {
    if (s.program?.heroMedia?.url) return s.program.heroMedia.url;
    const media = rootState.offer?.media || [];
    const found = media.find(m => (m.location === 'Header' || m.location === 'Hero') && m.url);
    return found?.url || null;
  },
  programTz: (s, g, rootState) => s.program?.timezone || rootState.partner?.timezone || 'America/Denver',
  ctx: (s, g) => {
    const tz = g.programTz;
    const m = s.program.startDate ? moment.tz(s.program.startDate, 'YYYY-MM-DD', tz) : moment.tz(tz);
    return buildCadenceCtx(s.cadence, { ...s.program, startDate: new Date(m.year(), m.month(), m.date()) });
  },
  participatingVenues: (s, g, rootState, rootGetters) => {
    const tz = g.programTz;
    const storeVenues = rootState.venues?.byId || {};
    const prizeIds = new Set(s.prizeProductIds || []);
    return s.venues.map(v => {
      const stored = storeVenues[v.id];
      const weekly = stored ? googleToWeekly(stored.hours) : null;
      const storeCoupons = (rootGetters['products/byVenueId'](v.id, s.dealId) || [])
        .filter(p => p.type === 'coupon' && !prizeIds.has(p.id))
        .map(p => {
          let settings = {};
          try { settings = typeof p.settings === 'string' ? JSON.parse(p.settings) : (p.settings || {}); } catch (e) {}
          return {
            productId: p.id,
            headline: p.name,
            sub: settings.sub_line ?? p.description,
            expires: settings.expires_on ?? null,
            state: couponState(p, rootGetters['deals/find'](s.dealId))
          };
        });
      return {
        ...v,
        name: stored?.name,
        description: stored?.description,
        img: v.img || firstMediaUrl(stored?.media) || stored?.gallery_bg,
        address: stored?.address,
        phone: stored?.phone,
        website: stored?.website,
        latitude: stored?.latitude,
        longitude: stored?.longitude,
        kinds: (stored?.categories || []).map(c => c?.name ?? c?.label ?? c).filter(x => typeof x === 'string'),
        hoursWeekly: weekly,
        weekdayText: stored?.hours?.weekday_text || [],
        hours: weekly ? summarizeWeek(weekly) : '',
        coupons: storeCoupons,
        ...periodLabels(v.startsAt, v.featuredDay, s.cadence, tz)
      };
    });
  },
  todayVenue: (s, g) => {
    const list = g.participatingVenues;
    return list.find(v => v.featuredDay === g.ctx.currentPeriod) || list[0];
  },
  effectiveCheckIn: (s, g) => g.todayVenue?.checkIn || null,
  currentPrize: (s, g) => g.todayVenue?.prize || null,
  productCheckIn: (s, g) => g.currentPrize?.checkIn || null,
  productTerms: (s, g) => g.currentPrize?.terms || {},
  // The REAL prize order product in the exact shape modules/Tickets expects.
  prizeProductId: s => s.prizeTicket?.productId ?? null,
  // Program-editable check-in failure-recovery copy. NEVER hardcode this copy in
  // the components — OS settings UIs drift, so it must stay config.
  recoveryCopy: s => s.program?.recoveryCopy || DEFAULT_RECOVERY_COPY,
  // Full products.ticket_settings shape, so TicketsModal + helpers see the real contract.
  ticketSettings: s => s.prizeTicket?.ticketSettings || ({
    show_ticket: { type: 'redemption' },
    hide_ticket: {
      after_redemption: { type: null, amount: '1', timer: false, message: '' },
      after_expiration: { type: null, amount: '1', message: '' }
    }
  }),
  // undefined = never hide; otherwise ms from redeemedAt until the ticket hides.
  ticketHideMs: (s, g) => ticketHideTimeoutMs(g.ticketSettings?.hide_ticket?.after_redemption),
  // Program-wide prize pool size — the denominator of the header chip and the meter.
  totalPrizePool: s => s.totalPrizes,
  prizesLeft: s => Math.max(0, s.totalPrizes - s.claimed),
  // Meter shows what's LEFT (urgency for the user, not celebration of depletion).
  meterPct: (s, g) => (g.totalPrizePool ? Math.round((g.prizesLeft / g.totalPrizePool) * 100) : 0),
  poolExhausted: (s, g) => g.prizesLeft === 0
};

const mutations = {
  SET_STATE(s, payload) {
    Object.assign(s, payload);
  },
  RESET(s) {
    // The held backend payload survives a reset: leaving the deal page must
    // not force a full pass-data reload to come back.
    const payload = s.payload;
    Object.assign(s, getDefaultState(), { payload });
  }
};

let pendingWinRefresh = null;

const actions = {
  /** Initial pass data delivered a giveaway section — hold it (not active yet). */
  setPayload({ commit }, payload) {
    commit('SET_STATE', { payload });
  },
  /** The deal page for the giveaway's own deal is open — go live. */
  activate({ commit, state: s, dispatch }) {
    if (!s.payload) return;
    commit('SET_STATE', payloadPatch(s.payload));
    dispatch('loadPrizeTicket');
  },
  /** Re-fetch the giveaway section (after an entry, a redemption or a period roll). */
  async refresh({ commit, state: s, rootState, dispatch }) {
    const partnerUserId = rootState?.partnerUser?.id;
    if (!s.dealId || !partnerUserId) return;
    const { data } = await $api.giveaway.get(partnerUserId, s.dealId);
    commit('SET_STATE', { payload: data, ...payloadPatch(data) });
    dispatch('loadPrizeTicket');
  },
  loadPrizeTicket({ state: s, dispatch }) {
    const prize = s.prizeTicket;
    if (!prize?.orderProductId || !prize?.productId) return;
    dispatch('products/addProducts', [{
      id: prize.productId,
      name: prize.productName,
      ticket_settings: prize.ticketSettings || {},
      redemption_settings: prize.redemptionSettings || {},
      redemption_policy: prize.redemptionPolicy || {},
      settings: prize.settings || {},
      redeem_on_dashboard: prize.redeemOnDashboard,
      redemptions: prize.redemptions
    }], { root: true });
    dispatch('orderProducts/addOrderProducts', [{
      id: prize.orderProductId,
      product_id: prize.productId,
      redeemed: prize.redeemed,
      end_date: prize.endDate,
      redemptions: prize.redemptions,
      ticket: {
        type: prize.ticket?.type,
        barcode_type: prize.ticket?.barcode_type,
        value: prize.ticket?.value,
        line_1: prize.ticket?.line_1,
        line_2: prize.ticket?.line_2
      }
    }], { root: true });
  },
  /**
   * Live entry: the check-in endpoint validates by the venue's configured
   * method SERVER-SIDE and runs the draw in the same call (ENG-2206).
   * `params` carries the validation payload collected by the check-in sheet
   * (pinNumber / latitude+longitude / answer / code). Returns { ok, message }
   * for the sheet bodies; on ok the outcome is committed and the body's
   * success beat then opens the reveal via checkInSuccess.
   */
  async submitEntry({ commit, getters: g, rootState, dispatch }, params = {}) {
    const spec = g.effectiveCheckIn;
    commit('SET_STATE', { submitting: true });
    try {
      const response = await $api.products.redeemProduct({
        type: 'check-in',
        productId: spec?.productId,
        venueId: g.todayVenue?.id,
        offerId: rootState?.offer?.id,
        partnerUserId: rootState?.partnerUser?.id,
        ...params
      }, rootState);
      const outcome = response.data?.giveaway;
      if (outcome) {
        commit('SET_STATE', {
          pendingOutcome: outcome.result,
          expiresAtData: outcome.win?.expiresAt ? new Date(outcome.win.expiresAt).getTime() : null
        });
        // Pull the real prize ticket + updated meter behind the reveal.
        pendingWinRefresh = dispatch('refreshQuietly');
      }
      return { ok: true };
    } catch (e) {
      return { ok: false, message: e?.response?.data?.message };
    } finally {
      commit('SET_STATE', { submitting: false });
    }
  },
  /**
   * Validation-only round trip (validate=true executes nothing): step 1 of a
   * gps_and_* combo checks the location server-side before the secondary
   * challenge; the final submitEntry re-validates both and enters.
   */
  async validateEntry({ getters: g, rootState }, params = {}) {
    const spec = g.effectiveCheckIn;
    try {
      await $api.products.redeemProduct({
        type: 'check-in',
        validate: true,
        productId: spec?.productId,
        venueId: g.todayVenue?.id,
        offerId: rootState?.offer?.id,
        partnerUserId: rootState?.partnerUser?.id,
        ...params
      }, rootState);
      return { ok: true };
    } catch (e) {
      return { ok: false, message: e?.response?.data?.message };
    }
  },
  /**
   * In-person prize redemption (ENG-2207): the existing redeem endpoint
   * validates by the prize product's configured method server-side; the
   * GiveawayDraw rule settles the win behind it.
   */
  async redeemCoupon({ dispatch, rootState }, { orderProductId, redemptions, ...params } = {}) {
    if (!orderProductId) return { ok: false, message: 'No coupon to redeem.' };
    try {
      const { data } = await $api.products.redeemProduct({
        type: redemptions || 'standard',
        orderProductIds: [orderProductId],
        partnerUserId: rootState?.partnerUser?.id,
        offerId: rootState?.offer?.id,
        ...params
      }, rootState);
      const errors = data?.errors;
      if (errors?.length) return { ok: false, message: errors[0] };
      if (data?.order_products?.length) {
        dispatch('orderProducts/update', data.order_products, { root: true });
      } else {
        dispatch('orderProducts/setRedeemed', orderProductId, { root: true });
      }
      return { ok: true };
    } catch (e) {
      return { ok: false, message: e?.response?.data?.message };
    }
  },
  async submitPrizeRedemption({ state: s, commit, dispatch, rootState }, params = {}) {
    if (pendingWinRefresh) { try { await pendingWinRefresh; } finally { pendingWinRefresh = null; } }
    const orderProductId = s.prizeTicket?.orderProductId;
    if (!orderProductId) return { ok: false, message: 'No prize to redeem.' };
    try {
      const { data } = await $api.products.redeemProduct({
        type: 'redeem',
        orderProductIds: [orderProductId],
        partnerUserId: rootState?.partnerUser?.id,
        offerId: rootState?.offer?.id,
        ...params
      }, rootState);
      const errors = data?.errors;
      if (errors?.length) return { ok: false, message: errors[0] };
      if (!data?.order_products?.length) return { ok: false, message: 'Redemption could not be confirmed.' };
      dispatch('orderProducts/update', data.order_products, { root: true });
      commit('SET_STATE', { redeemed: true, redeemedAt: Date.now() });
      return { ok: true };
    } catch (e) {
      return { ok: false, message: e?.response?.data?.message };
    }
  },
  async refreshQuietly({ dispatch }) {
    try {
      await dispatch('refresh');
    } catch (e) {
      // Non-fatal: the pass keeps the locally-known state until the next load.
    }
  },
  setStage({ commit }, stage) {
    commit('SET_STATE', { stage });
  },
  startCheckIn({ commit, state: s, getters: g }) {
    if (s.todayResult !== 'unentered' || g.poolExhausted) return;
    commit('SET_STATE', { stage: 'checkin' });
  },
  // Sheet success → open the reveal. The outcome was already committed by
  // submitEntry (the server's draw); this only advances the stage.
  checkInSuccess({ commit, state: s }) {
    if (s.pendingOutcome) commit('SET_STATE', { stage: 'reveal' });
  },
  persistOutcome({ commit, state: s }) {
    if (s.pendingOutcome === 'won') {
      commit('SET_STATE', {
        todayResult: 'won',
        wonAt: Date.now(),
        pendingOutcome: null
      });
    } else {
      commit('SET_STATE', { todayResult: 'lost', pendingOutcome: null });
    }
  },
  commitReveal({ dispatch, commit, state: s }) {
    dispatch('persistOutcome');
    // Lost: the reveal card already delivered the (single, warm) lose moment —
    // return straight to the pass, no second lose sheet.
    commit('SET_STATE', { stage: s.pendingOutcome === 'won' || s.todayResult === 'won' ? 'product' : 'idle' });
  },
  // X on the reveal: persist the outcome but return to the pass (source
  // dismissReveal) — a win stays redeemable from the featured card.
  dismissReveal({ dispatch, commit }) {
    dispatch('persistOutcome');
    commit('SET_STATE', { stage: 'idle' });
  },
  openReward({ commit }) {
    commit('SET_STATE', { stage: 'product' });
  },
  redeemFromTerms({ commit, getters: g }) {
    commit('SET_STATE', { stage: g.productCheckIn ? 'product-validate' : 'product-confirm' });
  },
  /**
   * Confirm-only prizes (gift-card: no in-person validation) settle
   * SERVER-SIDE the moment the code is revealed — otherwise the expiry cron
   * voids a revealed code and returns its prize to the pool. The redeem endpoint redeems
   * the validation-less prize directly; the GiveawayDraw rule settles the win.
   */
  async confirmRevealPrize({ commit, dispatch }) {
    const res = await dispatch('submitPrizeRedemption', {});
    if (res?.ok) commit('SET_STATE', { stage: 'ticket' });
    return res;
  },
  closeStage({ commit }) {
    commit('SET_STATE', { stage: 'idle' });
  },
  expireWin({ commit, dispatch }) {
    commit('SET_STATE', {
      todayResult: 'expired',
      wonAt: null,
      stage: 'idle'
    });
    // Server-side the cron voids the win and returns the prize to the pool —
    // resync so the meter and re-entry state match.
    dispatch('refreshQuietly');
  },
  resetToday({ commit }) {
    commit('SET_STATE', { todayResult: 'unentered', wonAt: null, redeemed: false, redeemedAt: null, pendingOutcome: null, stage: 'idle' });
  },
  reset({ commit }) {
    commit('RESET');
  }
};

export default { namespaced: true, state, getters, mutations, actions };
